import {
  Avatar,
  Badge,
  Button,
  DatePicker,
  Form,
  Modal,
  Segmented,
  Select,
  Switch,
} from "antd";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../app/hooks";
import { DownloadOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import styles from "./CreateReportForm.module.css";
import { IReportRequest } from "../../../interfaces/requestReport.interface";
import { Language } from "../../../interfaces/language.enum";
import { LocalDateAsUTC } from "../../../utils/LocalDateAsUTC";
import { SegmentedValue } from "antd/lib/segmented";
import { selectUser } from "../../access-control/accountSlice";
import dayjs from "dayjs";
import {
  IReport,
  IReportResponse,
} from "../../../interfaces/reports.interface";
import { TimeFormat } from "../../../interfaces/timeFormat.enum";
import CustomDateRangePicker from "../custom-date-range-picker/CustomDateRangePicker";

interface IPropsCreateReportForm {
  onSubmit: (
    reportRequest: IReportRequest,
    reportType?: string
  ) => Promise<IReportResponse>;
  reportOptions: IReport[];

  defaultCustomTimeOffset?: number;
  allowEmailSending?: boolean;
  languageOptions?: { value: Language; label: string }[];
}

let timeFormatOptions: { value: TimeFormat; label: string }[] = [
  {
    value: "today",
    label: "Today",
  },
  {
    value: "yesterday",
    label: "Yesterday",
  },
  {
    value: "thisWeek",
    label: "This Week",
  },
  {
    value: "lastWeek",
    label: "Last Week",
  },
  {
    value: "thisMonth",
    label: "This Month",
  },
  {
    value: "lastMonth",
    label: "Last Month",
  },
  {
    value: "custom",
    label: "Custom",
  },
];
const reportFormFieldLayout = {
  labelCol: { span: 5 },
  wrapperCol: { span: 12 },
};
export default function CreateReportForm({
  onSubmit,
  reportOptions,
  defaultCustomTimeOffset = 0,
  allowEmailSending = false,
  languageOptions,
}: IPropsCreateReportForm) {
  const [loading, setLoading] = useState(false);
  const user = useAppSelector(selectUser);
  const [report, setReport] = useState<IReport>(reportOptions[0]);
  const [form] = Form.useForm();
  const [timeFormat, setTimeFormat] = useState<TimeFormat>();
  const [url, setUrl] = useState<string | undefined>();
  const [noDataFound, setNoDataFound] = useState(false);
  const [isSentToEmail, setIsSentToEmail] = useState(false);

  useEffect(() => {
    form.setFieldsValue({
      dateRange: [
        dayjs()
          .subtract(1, "day")
          .hour(defaultCustomTimeOffset || 0)
          .minute(0)
          .second(0),
        dayjs()
          .hour(defaultCustomTimeOffset - 1 || 23)
          .minute(59)
          .second(59),
      ],
    });
  }, [report.type]);

  // const handleReset = () => {
  //   setLoading(false);
  //   setUrl(undefined);
  //   // setReport({ ...ReportOptions[0] });
  //   setNoDataFound(false);
  //   setIsSentToEmail(false);
  //   form.resetFields();
  // };

  const handleSubmit = async (
    values: Partial<IReportRequest> & { dateRange?: dayjs.Dayjs[] }
  ) => {
    setUrl(undefined);
    setNoDataFound(false);
    setLoading(true);
    setIsSentToEmail(false);

    let reportRequest: IReportRequest = {
      formatType: values.formatType!,
      timeFormat: values.timeFormat,
      startDate: values.startDate || "",
      endDate: values.endDate || "",
    };

    if (values.language) {
      reportRequest.language = values.language as Language;
    }

    if (values.dateRange && values.timeFormat === "custom") {
      const [start, end] = values.dateRange;
      const startDate = start.millisecond(0).second(0);
      const endDate = end.millisecond(999).second(59);
      reportRequest.startDate = LocalDateAsUTC.getISOFromDate(
        startDate.toDate()
      );
      reportRequest.endDate = LocalDateAsUTC.getISOFromDate(endDate.toDate());
    }

    reportRequest.receiveViaEmail = values.receiveViaEmail;

    if (values.receiveViaEmail) {
      reportRequest.receiveViaEmail = true;
      reportRequest.email = user.email;
    }

    let results = {} as {
      transactionsReport?: string | null;
      report?: string | null;
      isEmailed: boolean;
      isEmpty: boolean;
    };

    results = await onSubmit(reportRequest, report?.type);
    if (results?.isEmpty) setNoDataFound(true);

    if (results?.isEmailed) setIsSentToEmail(true);
    setUrl(results?.transactionsReport || results?.report || "");
    setLoading(false);
  };

  const handleTimeFormatChange = (value: TimeFormat) => {
    setTimeFormat(value);
  };

  const handleReportChange = (value: SegmentedValue) => {
    let report = reportOptions.find((r) => value === r.type);
    if (!report) return;
    setReport({ ...report });
    form.setFieldsValue({ formatType: report.formatTypes[0] });
  };

  const getReportCard = (report: IReport) => (
    <div style={{ padding: 4 }}>
      <Avatar
        style={{
          width: "50px",
          height: "50px",
          marginTop: "10px",
          backgroundColor: "#e2bee8",
        }}
        size={"large"}
        src={report.previewUrl || ""}
      />
      <div className={styles.report_card}>
        <span>{report.label}</span>
      </div>
    </div>
  );

  const getLanguageFlag = (lang?: Language) => {
    switch (lang) {
      case Language.Italian:
        return "🇮🇹";
      case Language.Dutch:
        return "🇳🇱";
      case Language.German:
        return "🇩🇪";
      case Language.English:
        return "🇬🇧";
      case Language.French:
        return "🇫🇷";
      default:
        return "";
    }
  };

  return (
    <div>
      <div className={styles.report_cards_container}>
        <Segmented
          onChange={handleReportChange}
          value={report?.type}
          options={reportOptions.map((r) => ({
            label: r.isBeta ? (
              <Badge.Ribbon text="Beta" color="purple">
                {getReportCard(r)}
              </Badge.Ribbon>
            ) : (
              <div style={{ padding: 4 }}>
                <Avatar
                  style={{ width: "50px", height: "auto", paddingTop: "10px" }}
                  size={"large"}
                  src={r.previewUrl}
                />
                <div className={styles.report_card}>
                  <span>{r.label}</span>
                </div>
              </div>
            ),
            value: r.type,
            key: r.type,
          }))}
        />
      </div>
      <br></br>
      {report.warning && (
        <div className={styles.report_warning}>{report.warning}</div>
      )}
      <div className={styles.report_description}>{report.description}</div>

      <br></br>
      <br></br>
      {report?.type ? (
        <Form onFinish={handleSubmit} form={form}>
          <Form.Item
            name="formatType"
            initialValue={report.formatTypes[0]}
            label="Format Type"
            labelCol={{ ...reportFormFieldLayout.labelCol }}
            labelAlign="left"
          >
            <Select
              style={{ width: "200px" }}
              options={report.formatTypes.map((ft) => ({
                value: ft,
                label: ft,
                key: `${report.type}+${ft}`,
              }))}
            ></Select>
          </Form.Item>
          {languageOptions && (
            <Form.Item
              name="language"
              label="Language"
              labelCol={{ ...reportFormFieldLayout.labelCol }}
              labelAlign="left"
              initialValue={languageOptions?.[0]?.value}
            >
              <Select
                style={{ width: "200px" }}
                options={languageOptions?.map((opt) => ({
                  value: opt.value,
                  label: (
                    <span>
                      <span style={{ marginRight: 8 }}>
                        {getLanguageFlag(opt.value)}
                      </span>
                      {opt.label}
                    </span>
                  ),
                  key: `${report.type}+${opt.value}`,
                }))}
              />
            </Form.Item>
          )}
          <Form.Item
            initialValue={"yesterday"}
            name="timeFormat"
            label="Time Format"
            labelCol={{ ...reportFormFieldLayout.labelCol }}
            labelAlign="left"
          >
            <Select
              style={{ width: "200px" }}
              onChange={handleTimeFormatChange}
              options={timeFormatOptions.map((t) => ({
                value: t.value,
                label: t.label,
                key: `${report.type}+${t.label}`,
              }))}
            ></Select>
          </Form.Item>
          {timeFormat === "custom" && (
            <Form.Item
              name="dateRange"
              label={
                report.type === "payoutReport"
                  ? "Payout Dates"
                  : "Purchase Dates"
              }
              labelCol={{ ...reportFormFieldLayout.labelCol }}
              labelAlign="left"
            >
              <CustomDateRangePicker
                placeholder={
                  report.type === "payoutReport"
                    ? ["Start Payout Date", "End Payout Date"]
                    : ["Start Purchase Date", "End Purchase Date"]
                }
              />
            </Form.Item>
          )}

          {allowEmailSending && (
            <Form.Item
              name="receiveViaEmail"
              label={"Receive Via Email"}
              labelCol={{ ...reportFormFieldLayout.labelCol }}
              labelAlign="left"
              tooltip="Large reports (e.g. Accounting for high-volume merchants) can exceed the download timeout. Email delivery builds the report in the background and is not time-limited."
            >
              <Switch
                defaultChecked={false}
                checkedChildren="Yes"
                unCheckedChildren="No"
              />
            </Form.Item>
          )}

          <Form.Item>
            <Button
              style={{ float: "right" }}
              type="primary"
              htmlType="submit"
              loading={loading}
            >
              Create Report
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <>Please pick a report type.</>
      )}

      <div className={styles.results_container}>
        {/* {loading && <Spin></Spin>} */}
        {url && (
          <div>
            <Button
              type="link"
              icon={<DownloadOutlined />}
              href={url}
              target="_blank"
              style={{ width: "240px", fontSize: "14px" }}
            >
              Click here to view report
            </Button>
          </div>
        )}

        {isSentToEmail && (
          <div>
            Report was sent to the following email address{" "}
            <a href={user.email}>{user.email}</a>
          </div>
        )}
        {noDataFound && (
          <div>
            <ExclamationCircleOutlined
              style={{ margin: "0 10px", color: "#f5b642" }}
            />
            No data was found in the selected time period. Try selecting a
            different time period.
          </div>
        )}
      </div>
    </div>
  );
}
